import React, { useEffect, useState } from "react";
import { FiArrowLeft, FiArrowRight, FiArrowUpRight, FiTrendingUp, FiZap, FiStar } from "react-icons/fi";
import { connectors } from "../data/connectors";
import type { ConnectorCategory } from "../data/connectors";
import { ConnectorLogo } from "./ConnectorLogo";

type Props={onExplore:(category:ConnectorCategory|"All")=>void};
const slides=[
  {id:"popular",label:"POPULAR STARTING POINTS",title:"Start with the platforms you know.",
    text:"Connect identity, wellbeing and culture to begin building your personal data graph.",
    kind:"Popular picks",category:"All" as const,keys:["google","whoop","letterboxd"],icon:FiStar,tone:"cyan"},
  {id:"trending",label:"TRENDING COMBINATIONS",title:"One person. New connections.",
    text:"Explore the intersections of fitness, film and music as the dataset economy grows.",
    kind:"Exploration ideas",category:"Entertainment" as const,keys:["strava","letterboxd","spotify"],icon:FiTrendingUp,tone:"violet"},
  {id:"value",label:"HIGH-POTENTIAL SIGNALS",title:"The value of being current.",
    text:"Receipt evidence and recurring confirmations could give buyers fresher, permissioned signals.",
    kind:"Potential value",category:"Streaming" as const,keys:["receipt","netflix","hulu"],icon:FiZap,tone:"lime"},
  {id:"fantasy",label:"FANTASY × GAMING",title:"Understand how people play.",
    text:"Bring fantasy sports and gaming identity into a single, permissioned interest graph.",
    kind:"Fan communities",category:"Fantasy sports" as const,keys:["sleeper","espnfantasy","xbox"],icon:FiStar,tone:"violet"},
  {id:"finance",label:"CONSENT-BASED FINANCE",title:"Verification without passwords.",
    text:"Financial institutions belong in Handle only through secure, approved links and purpose-limited sharing.",
    kind:"Financial identity",category:"Banking" as const,keys:["chase","capitalone","amex"],icon:FiZap,tone:"cyan"},
];
export const SourceCarousel:React.FC<Props>=({onExplore})=>{
  const [active,setActive]=useState(0);
  const [paused,setPaused]=useState(false);
  useEffect(()=>{
    if(paused || typeof window==="undefined" || window.matchMedia("(prefers-reduced-motion: reduce)").matches)return;
    const timer=window.setInterval(()=>setActive(i=>(i+1)%slides.length),7000);
    return ()=>window.clearInterval(timer);
  },[paused]);
  const slide=slides[active];
  const Icon=slide.icon;
  return <section className="h-discovery" aria-label="Discover data connections" onMouseEnter={()=>setPaused(true)}
    onMouseLeave={()=>setPaused(false)} onFocusCapture={()=>setPaused(true)}
    onBlurCapture={event=>{if(!event.currentTarget.contains(event.relatedTarget as Node))setPaused(false);}}>
    <div className="h-section-heading"><div><span className="h-small-label">DISCOVER</span><h2>What's possible with your data?</h2><p>Explore editorial picks and new ways to connect your world.</p></div>
      <span className="h-pill h-pill-info">Curated examples · Not usage rankings</span>
    </div>
    <div className={"h-discovery-slide h-panel h-discovery-"+slide.tone} role="region"
      aria-roledescription="carousel" aria-label="Connector discovery highlights">
      <div className="h-discovery-copy" key={slide.id}>
        <span className="h-eyebrow"><Icon size={15}/>{slide.label}</span>
        <h3>{slide.title}</h3><p>{slide.text}</p>
        <button type="button" className="h-btn h-btn-primary" onClick={()=>onExplore(slide.category)}>Explore sources <FiArrowUpRight size={16}/></button>
      </div>
      <div className="h-discovery-apps" aria-label="Highlighted connectors" key={slide.id+"-apps"}>
        {slide.keys.map((key,index)=>{const connector=connectors.find(c=>c.key===key);return connector?
          <div className={"h-discovery-app h-discovery-app-"+index} key={key}><ConnectorLogo connector={connector}/><span>{connector.name}</span></div>:null;})}
      </div>
    </div>
    <div className="h-discovery-pagination">
      <span>{String(active+1).padStart(2,"0")} / {String(slides.length).padStart(2,"0")}</span>
      <div className="h-discovery-dots">{slides.map((s,i)=><button type="button" key={s.id} aria-label={"Show "+s.kind}
        aria-current={i===active?"true":undefined} className={i===active?"active":""} onClick={()=>setActive(i)}/>)}</div>
      <div className="h-discovery-arrows">
        <button type="button" aria-label="Previous highlight" onClick={()=>setActive(i=>(i+slides.length-1)%slides.length)}><FiArrowLeft/></button>
        <button type="button" aria-label="Next highlight" onClick={()=>setActive(i=>(i+1)%slides.length)}><FiArrowRight/></button>
      </div>
    </div>
  </section>;
};
